import { FormEvent } from "react";
import { FormikErrors } from "formik";
import { Button, Image, Input } from "@atoms";
import ReactModal from "react-modal";
import { styles as globalStyles } from "../../../styles/styles";
import { AuthSection } from "@organisms";
import { useTranslation } from "react-i18next";

export interface FormValues {
  password: string;
  repeatPassword: string;
}

export type ForceResetPasswordPageTemplateProps = {
  values: FormValues;
  errors: FormikErrors<FormValues>;
  loading: boolean;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSignInRedirect: () => void;
  handleSubmit: (e?: FormEvent<HTMLFormElement> | undefined) => void;
  onCloseModal: () => void;
  showSuccessModal: boolean;

};

export const ForceResetPasswordPageTemplate: React.FC<ForceResetPasswordPageTemplateProps> = ({
  values,
  errors,
  loading,
  handleInputChange,
  handleSignInRedirect,
  handleSubmit,
  onCloseModal,
  showSuccessModal
}: ForceResetPasswordPageTemplateProps): JSX.Element => {
  const { t } = useTranslation();
  const { password, repeatPassword } = values;
  const { password: passwordError, repeatPassword: repeatPasswordError } = errors;

  return (
    <div className="min-h-screen flex">
      <AuthSection />

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 min-h-screen flex items-center justify-center bg-white">
        <div className="w-full max-w-md px-8">
          {/* Mobile logo */}
          <div className="lg:hidden absolute top-8 left-8">
            <div className="flex items-center gap-2">
              <Image variant="parachuteLogo" height={32} width={32} />
              <span className="text-neutral-darkest text-xl font-medium">Parachute</span>
            </div>
          </div>

          <form
            className="text-center flex flex-col items-center max-w-xs mx-auto"
            onSubmit={handleSubmit}
          >
            <div className="px-3 mb-2">
              <div className="w-80">
                <h3 className="text-3xl font-black text-neutral-darkest">{t("auth.forceResetPassword.title")}</h3>
              </div>
            </div>
            <div className="px-3 mb-6">
              <div className="mb-2">
                <h3 className="text-md text-gray-600">
                  {t("auth.forceResetPassword.subtitle")}
                </h3>
              </div>
            </div>
            <div className="w-full">
              <div className="mb-2">
                <Input
                  name="password"
                  variant="password"
                  onChange={handleInputChange}
                  placeholder={t("auth.forceResetPassword.newPassword")}
                  value={password}
                  errorMessage={passwordError}
                />
              </div>
            </div>
            <div className="w-full">
              <div className="mb-2">
                <Input
                  name="repeatPassword"
                  variant="password"
                  onChange={handleInputChange}
                  placeholder={t("auth.forceResetPassword.confirmPassword")}
                  value={repeatPassword}
                  errorMessage={repeatPasswordError}
                />
              </div>
            </div>
            <div className="mt-2 mb-6 w-full sm:mb-4">
              <div>
                <Button
                  type="submit"
                  variant="primary"
                  label={t("auth.forceResetPassword.submit")}
                  stretch
                  color="neutral-darkest"
                  hoverColor="neutral-dark"
                  textColor="white"
                  loading={loading}
                />
              </div>
            </div>
              <div className="mb-6 w-full sm:mb-4">
                <div>
                  <Button
                    variant="secondary"
                    label={t("auth.forceResetPassword.backToSignIn")}
                    stretch
                    color="neutral-darkest"
                    onClick={handleSignInRedirect}
                  />
                </div>
            </div>
            <ReactModal
              isOpen={showSuccessModal}
              onRequestClose={onCloseModal}
              style={globalStyles.generalModal}
              shouldCloseOnOverlayClick={true}
              ariaHideApp={false}
              contentLabel="Password Reset Modal"
            >
              <div className="font-medium text-neutral-darkest text-center text-xl pt-4">{t("auth.forceResetPassword.successTitle")}</div>
              <div className="font-normal text-neutral-dark text-base text-center pt-1 pb-8">{t("auth.forceResetPassword.successDescription")}</div>             
              <div className="flex flex-row justify-center pt-4">
                <div className="w-40">   
                  <Button
                    type="submit"
                    onClick={onCloseModal}
                    variant="primary"
                    label="OK"
                    stretch
                    color="neutral-darkest"
                    hoverColor="neutral-dark"
                    textColor="white"
                    textWeight="semibold"
                  /> 
                </div>
              </div>    
            </ReactModal>
          </form>
        </div>
      </div>
    </div>
  );
};
